import { computed, getCurrentScope, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue'
import { useRouter } from 'vue-router'

import { ImageBuildStatus, ImageBuildStep } from '@/enums/image'
import i18n from '@/libs/i18n'
import { useFetchStore } from '@/stores/fetch'
import type { TagItem } from '@/types/Common'
import type { IImageStatus } from '@/types/Image'

const t = (key: string): string => i18n.global.t(key)

// How often GET /image is re-read while a build is in flight. A build takes tens of
// minutes, so this is not about catching the end of it quickly — it is about the bar
// moving, which is the only thing that tells somebody waiting that it is still alive.
export const ImagePollInterval = 3_000

// How often it is re-read while nothing is building. A build can START under a tab that
// has been open for hours — an admin presses Build new, the boot gate fires after an
// upgrade — and the whole app becomes one page when it does (invariant 27, the gate), so
// the shell keeps asking at a rhythm that costs nothing: one small GET every 30 seconds.
export const ImageIdlePollInterval = 30_000

// The ten steps, in words. A key this build does not know — an older SPA against a newer
// plane — reads as an empty label rather than as the raw key, because a card that says
// `push_inputs` is worse than one that says only how far along it is.
export const getImageStepLabel = (step: string): string => {
    const known: string[] = Object.values(ImageBuildStep)

    return step && known.includes(step) ? t(`image.step.${step}`) : ''
}

// The badge of the last build attempt, for the admin card. `skipped` is its own outcome:
// the plane recognised the inputs as the pair that already failed and built nothing, which
// is a failure that has stopped repeating itself rather than a fresh one.
export const getImageBuildStatus = (status: string): TagItem => {
    const map: Record<string, TagItem> = {
        [ImageBuildStatus.Building]:  { label: t('image.status.building'), color: 'info', icon: 'i-lucide-loader-circle' },
        [ImageBuildStatus.Succeeded]: { label: t('image.status.succeeded'), color: 'success', icon: 'i-lucide-circle-check' },
        [ImageBuildStatus.Failed]:    { label: t('image.status.failed'), color: 'error', icon: 'i-lucide-circle-x' },
        [ImageBuildStatus.Skipped]:   { label: t('image.status.skipped'), color: 'warning', icon: 'i-lucide-circle-slash' },
    }

    return map[status] ?? { label: t('image.status.none'), color: 'neutral', icon: 'i-lucide-package' }
}

// Whether the last build is one the admin card reports as a failure. Both outcomes carry
// an error string, and both mean the same thing to the person reading the page: the image
// in the store is not the one this api ships inputs for.
export const isImageBuildFailure = (status: string): boolean =>
    status === ImageBuildStatus.Failed || status === ImageBuildStatus.Skipped

// ---------------------------------------------------------------------------------------
// The shared status
// ---------------------------------------------------------------------------------------

export interface ImageStatusState {
    status:    Ref<IImageStatus | null>
    loaded:    Ref<boolean>
    preparing: ComputedRef<boolean>
    building:  ComputedRef<boolean>
    percent:   ComputedRef<number | null>
    step:      ComputedRef<string>
    refresh:   () => Promise<IImageStatus | null>
}

// ONE poller for the whole app. The shell watches the gate for the whole time a tab is
// open, the preparing page watches the bar move and the admin card watches a build it
// started — a per-component poll would mean a request per consumer, all of them asking the
// same question. So the state is module-level and the consumers are counted: the first one
// starts the loop, the last one to go away stops it.
const status = ref<IImageStatus | null>(null)
const loaded = ref(false)

let consumers = 0
let timer: ReturnType<typeof setTimeout> | null = null
let inFlight: Promise<IImageStatus | null> | null = null

const preparing = computed(() => !!status.value?.preparing)
const building  = computed(() => status.value?.build?.status === ImageBuildStatus.Building)

// Null, not zero, while nothing is running: a `UProgress` with no value is the indeterminate
// bar, which is the honest picture of a boot gate that has not started its build yet.
const percent = computed((): number | null => building.value ? status.value?.build?.percent ?? 0 : null)

const step = computed((): string => getImageStepLabel(status.value?.build?.step ?? ''))

const stop = () => {
    if (!timer) return

    clearTimeout(timer)
    timer = null
}

// A chained timeout rather than an interval: the answer that just came back is what decides
// how soon the next one is worth asking for, and a slow plane can never stack two polls on
// top of each other. The loop runs for as long as somebody is watching — three seconds
// apart while a build is in flight, thirty while it is not, because the gate closing is as
// much news to an open tab as the bar moving is to somebody waiting in front of it.
const schedule = () => {
    stop()

    if (!consumers) return

    const delay = preparing.value || building.value ? ImagePollInterval : ImageIdlePollInterval

    timer = setTimeout(() => { void refreshImageStatus() }, delay)
}

const read = async (): Promise<IImageStatus | null> => {
    const fetchStore = useFetchStore()

    const { statusCode, data } = await fetchStore.fetch('/image').get().json<IImageStatus>()

    if (statusCode.value === 200 && data.value) status.value = data.value

    loaded.value = true

    return status.value
}

// No toast on a failure and no error state: the image status decides whether a button is
// disabled and whether a card is drawn, and a plane that did not answer must leave both
// exactly as they were rather than block every Start in the app.
export const refreshImageStatus = (): Promise<IImageStatus | null> => {
    if (inFlight) return inFlight

    stop()

    inFlight = read().finally(() => {
        inFlight = null
        schedule()
    })

    return inFlight
}

// The one way into the shared state. Every caller gets the same refs, so Home, a project
// card, the workspaces list and the admin card can never disagree about whether the image
// is being prepared.
export const useImageStatus = (): ImageStatusState => {
    consumers++

    if (loaded.value) schedule()
    else void refreshImageStatus()

    // Bound to the calling component's scope, so the count goes back down on unmount. A
    // caller outside a scope (a lib, a one-off read) simply never gives its slot back,
    // which is why only components use this.
    if (getCurrentScope()) {
        onScopeDispose(() => {
            consumers = Math.max(0, consumers - 1)
            if (!consumers) stop()
        })
    }

    return { status, loaded, preparing, building, percent, step, refresh: refreshImageStatus }
}

// ---------------------------------------------------------------------------------------
// The gate
// ---------------------------------------------------------------------------------------

// What the router asks before it lets a navigation through: is the plane preparing the
// workspace image? While it is, the app is ONE page and nothing else is reachable — for an
// administrator as much as for anybody else (invariant 27).
//
// The first call reads `GET /image`; after that the answer comes off the shared state the
// shell keeps polling, so a navigation costs nothing. A plane that did not answer reads as
// "not preparing": a gate nobody can confirm must not lock the app out of itself.
export const ensureImageStatus = async (): Promise<boolean> => {
    if (!loaded.value) await refreshImageStatus()

    return preparing.value
}

// The other way the gate closes: an api call refused with error code 14. The plane has just
// said it is building the image, which is more current than anything the poll holds, so the
// state is moved over at once and a real read is asked for right behind it to pick up the
// step and the percentage.
export const markImagePreparing = (): void => {
    status.value = {
        preparing: true,
        current:   status.value?.current ?? null,
        previous:  status.value?.previous ?? null,
        build:     status.value?.build ?? {
            status: '', step: '', percent: 0, version: '', started_at: '', finished_at: '', error: '', has_log: false,
        },
    }

    loaded.value = true

    void refreshImageStatus()
}

// Wired once by the shell. A build that starts under an open tab has to take the app over
// the same way a build in progress does at boot, and the poll above is what notices — so
// the one watcher that turns that into a navigation lives here, with the rest of the gate,
// rather than in a page that would have to remember to check.
export const useImageGate = (): void => {
    const router        = useRouter()
    const { preparing } = useImageStatus()

    watch(preparing, (value): void => {
        if (!value) return

        const current = router.currentRoute.value

        if (current.name === 'preparing') return

        void router.push({ name: 'preparing', query: { redirect: current.fullPath } })
    })
}
